import { useRef, useState } from "react";
import { observer } from "mobx-react";
import { Shortcut } from "../../../header/shortcuts/Shortcut";
import {
  conditionType,
  currentViewType,
} from "../../../modals/AdminModal/AdminModalTypes";
import ApiClient from "../../../../backends/ApiClient";
import NotificationManager from "../../../notifications/NotificationManager";
import styles from "./styles/ShortcutsTerrace.module.scss";
import classNames from "classnames";
import NavigationModel from "../../../../utils/NavigationModel";
import FontAwesome from "../../../utilities/FontAwesome";
import { addLeadingZeros } from "../../../../utils/DataUtilities";
import dynamic from "next/dynamic";
import BlockTitle from "../_BlockTitle";
import { EditDropDownItem } from "../EditDropDown";
import { useStores } from "../../../../stores/useStores";
import { capitalize } from "../../../../utils/StringUtilities";
import { runInAction } from "mobx";
import { Flex, SegmentedControl } from "@radix-ui/themes";
import {
  ScrollHint,
  scrollRowClassName,
  useScrollButtons,
} from "../ScrollHint";

export const enum shortcutsBackgroundOptions {
  THEME = "theme",
  LIGHT = "light",
}

const DraggableShortCut = dynamic(
  () => import("../../../header/shortcuts/DraggableShortcut"),
);

const ShortcutsTerrace = observer(
  (props: { blockObj: IShortcutsTerrace; aboveTheFold: boolean }) => {
    const { userStore, organizationStore, modalStore, gridStore } = useStores();
    const [isDragging, setIsDragging] = useState(false);
    const [reorder, setReorder] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const preDragSnapshotRef = useRef<Navigation[] | null>(null);
    const scroll = useScrollButtons();

    const themeColor =
      props.blockObj.json_data.shortcuts?.backgroundColor ===
      shortcutsBackgroundOptions.THEME;

    const shortcutsTerraceContainerClassName = classNames({
      [styles.container]: true,
      [styles.containerIsEditor]: userStore.isEditor,
      [styles.containerGreaterThanSix]: props.blockObj?.shortcuts?.length > 6,
      [styles.containerDragging]: isDragging,
    });

    const articleContainerClassName = classNames({
      [styles.articleContainer]: true,
      [styles.articleContainerThemeColor]: themeColor,
    });

    const reorderClassName = classNames({
      [styles.reorder]: reorder,
    });

    const navigationUpdater = async (navigation) => {
      try {
        const client = await ApiClient();
        const updatedNav = await (client.PATCH(
          "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/{id}/",
          {
            params: {
              path: {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
                id: navigation.id,
              },
            },
            body: navigation,
          },
        ) as any);

        props.blockObj.shortcuts = props.blockObj.shortcuts.map((link) => {
          if (link.id === navigation.id) {
            return updatedNav;
          }
          return link;
        });
      } catch {
        NotificationManager.error("Something went wrong! Please try again.");
      }
    };

    const updateNavigation = (navigation) => {
      modalStore.addModal({
        type: "admin",
        condition: conditionType.EDITING,
        nodeInfo: navigation,
        navigation: props.blockObj.shortcuts,
        terrace_id: props.blockObj.id,
        navigationUpdater,
      });
    };

    const deleteNavigation = async (id) => {
      if (confirm("Are you sure you want to do that?")) {
        try {
          const client = await ApiClient();
          // the spec documents no requestBody for this endpoint, but at
          // runtime the app has always sent the array of ids to delete (SBSB-2912)
          await client.DELETE(
            "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/bulk_destroy/",
            {
              params: {
                path: {
                  organization_pk: props.blockObj.organization_id,
                  terrace_pk: props.blockObj.id,
                },
              },
              body: [id] as never,
            },
          );
          props.blockObj.shortcuts = props.blockObj.shortcuts.filter(
            (n) => n.id !== id,
          );
        } catch {
          NotificationManager.error("Something went wrong! Please try again.");
        }
      }
    };

    const navigationCreator = async (navigation) => {
      try {
        const client = await ApiClient();
        const newNav = await (client.POST(
          "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/",
          {
            params: {
              path: {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
              },
            },
            body: navigation,
          },
        ) as any);

        props.blockObj.shortcuts = [...props.blockObj.shortcuts, newNav];
        return newNav;
      } catch {
        NotificationManager.error("Something went wrong! Please try again.");
      }
    };

    function onHoverCallback(list) {
      props.blockObj.shortcuts = list;
    }

    function onDragBegin() {
      preDragSnapshotRef.current = [...props.blockObj.shortcuts];
    }

    async function onDrop() {
      const snapshot = preDragSnapshotRef.current;
      preDragSnapshotRef.current = null;

      const orderUnchanged =
        snapshot &&
        snapshot.length === props.blockObj.shortcuts.length &&
        snapshot.every((s, i) => s.id === props.blockObj.shortcuts[i].id);
      if (orderUnchanged) return;

      for (let i = 0; i < props.blockObj.shortcuts.length; i++) {
        const newPath = `${props.blockObj.id}.${addLeadingZeros(i + 1)}`;
        props.blockObj.shortcuts[i].path = newPath;
      }
      try {
        const client = await ApiClient();
        await client.PUT(
          "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/bulk_update/",
          {
            params: {
              path: {
                organization_pk: organizationStore.currentOrganization.id,
                terrace_pk: props.blockObj.id,
              },
            },
            body: props.blockObj.shortcuts as never,
          },
        );
        NotificationManager.success("Shortcuts successfully updated.");
      } catch {
        if (snapshot) {
          runInAction(() => {
            props.blockObj.shortcuts = snapshot;
          });
        }
        NotificationManager.error(
          "Reorder didn't save — we put the shortcuts back the way they were. Please try again.",
          "Reorder failed",
          6000,
        );
      }
    }

    const addShortcut = () => {
      modalStore.addModal({
        type: "admin",
        condition: conditionType.ADD_TOPLINK,
        nodeInfo: new NavigationModel(),
        navigation: props.blockObj.shortcuts,
        initialView: currentViewType.shortcut,
        terrace_id: props.blockObj.id,
        navigationCreator,
      });
    };

    const reorderShortcutsDropDownItem = {
      title: "Toggle Reordering",
      icon: (
        <FontAwesome
          prefix={"fas"}
          fixedWidth
          name={"fa-arrows-left-right"}
          ariaHidden
          className={reorderClassName}
        />
      ),
      onClick: () => {
        setReorder((v) => !v);
        setCanEdit(false);
      },
      hideButtonSelector: true,
    };

    const editShortcutsDropDownItem = {
      title: canEdit ? "Close Editor" : "Edit",
      icon: <FontAwesome prefix={"fas"} fixedWidth className="fa-pencil-alt" />,
      onClick: () => {
        setCanEdit((v) => !v);
        setReorder(false);
      },
    };

    const shortcutsTerraceDropDownItems: EditDropDownItem[] = [
      {
        title: "Add Shortcut",
        icon: <FontAwesome prefix={"fas"} fixedWidth name={"fa-plus"} />,
        onClick: addShortcut,
      },
    ];

    if (props.blockObj?.shortcuts?.length > 0) {
      shortcutsTerraceDropDownItems.push(editShortcutsDropDownItem);
    }

    if (props.blockObj?.shortcuts?.length > 1) {
      shortcutsTerraceDropDownItems.push(reorderShortcutsDropDownItem);
    }

    const handleChange = async (val) => {
      try {
        const submitData = {
          ...props.blockObj.json_data,
          shortcuts: {
            ...props.blockObj.json_data.shortcuts,
            backgroundColor: val,
          },
        };
        const client = await ApiClient();
        const updatedTerrace = await client.PATCH(
          "/organizations/{organization_pk}/terraces/{id}/",
          {
            params: {
              path: {
                organization_pk: props.blockObj.organization_id,
                id: props.blockObj.id,
              },
            },
            body: {
              json_data: submitData,
            } as never,
          },
        );
        runInAction(() => {
          gridStore.blocks.set(updatedTerrace.id, updatedTerrace);
        });
        NotificationManager.success("Terrace successfully saved!");
      } catch {
        NotificationManager.error("Something went wrong! Please try again.");
      }
    };

    const backgroundColorOptions = [
      {
        value: shortcutsBackgroundOptions.THEME,
        label: capitalize(shortcutsBackgroundOptions.THEME),
      },
      {
        value: shortcutsBackgroundOptions.LIGHT,
        label: capitalize(shortcutsBackgroundOptions.LIGHT),
      },
    ];

    return (
      <article className={articleContainerClassName}>
        <BlockTitle
          blockObj={props.blockObj}
          aboveTheFold={props.aboveTheFold}
          allowSave={true}
          dropDownItems={shortcutsTerraceDropDownItems}
          editDropDownClassName={
            themeColor ? styles.editDropDownThemeColor : styles.editDropDown
          }
          blockTitleContainerClassName={
            themeColor ? styles.titleThemeColor : styles.title
          }
          showViewMore={false}
          rightElement={
            canEdit ? (
              <SegmentedControl.Root
                size={"2"}
                title={"Toggle Theme"}
                value={
                  props.blockObj.json_data.shortcuts?.backgroundColor ||
                  shortcutsBackgroundOptions.LIGHT
                }
                onValueChange={handleChange}
              >
                {backgroundColorOptions.map((option) => (
                  <SegmentedControl.Item
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            ) : scroll.overflows ? (
              <ScrollHint scroll={scroll}>Scroll for more</ScrollHint>
            ) : undefined
          }
        />
        <div className={shortcutsTerraceContainerClassName}>
          {reorder && (
            <div className={styles.reorderBanner} role="status">
              <FontAwesome
                prefix={"fas"}
                fixedWidth
                name={"fa-arrows-left-right"}
                ariaHidden
              />
              <span>Reordering on — drag shortcuts to rearrange.</span>
            </div>
          )}
          {props.blockObj?.shortcuts?.length > 0 && (
            <Flex asChild pb={"3"}>
              <ul
                ref={scroll.rowRef}
                className={scrollRowClassName}
                onScroll={scroll.onScroll}
              >
                {props.blockObj.shortcuts.map((link, index) => {
                  const iconClassName = classNames({
                    [styles.icon]: true,
                    [styles.iconReorder]: reorder,
                    [styles.iconEditing]: canEdit,
                  });

                  if (userStore.isEditor) {
                    return (
                      <DraggableShortCut
                        key={link.id}
                        className={iconClassName}
                        title={reorder ? "Reorder Shortcut" : undefined}
                        shortcut={link}
                        subNavigation={props.blockObj.shortcuts}
                        onHoverCallback={onHoverCallback}
                        index={index}
                        setIsDragging={setIsDragging}
                        reorder={reorder}
                        updateNavigation={updateNavigation}
                        deleteNavigation={deleteNavigation}
                        canEdit={canEdit}
                        onDrop={onDrop}
                        onDragBegin={onDragBegin}
                      />
                    );
                  } else {
                    return (
                      <Shortcut
                        key={link.id}
                        className={iconClassName}
                        shortcut={link}
                        updateNavigation={updateNavigation}
                        deleteNavigation={deleteNavigation}
                        canEdit={canEdit}
                      />
                    );
                  }
                })}
              </ul>
            </Flex>
          )}
        </div>
      </article>
    );
  },
);

export default ShortcutsTerrace;
